import React, { useState, useEffect } from 'react';
import { api } from '../utils/apiClient';
import './SeoTab.css';

const PAGE_SIZE = 20;

const SeoTab = ({ url: propUrl }) => {
  const [url, setUrl] = useState(propUrl || '');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [allPages, setAllPages] = useState(null);
  const [loadingAll, setLoadingAll] = useState(false);
  const [expanded, setExpanded] = useState({}); // { pageIdx: true/false }
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUrls, setTotalUrls] = useState(0);

  const analyze = async (analyzeUrl) => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const { data } = await api.post('/api/seo/seo-analyze', { url: analyzeUrl });
      setResult(data);
    } catch (err) {
      const message = err.response?.data?.error || err.response?.data?.message;
      setError(message || 'Failed to analyze SEO.');
    }
    setLoading(false);
  };

  const fetchAllPages = async (page = 1) => {
    setLoadingAll(true);
    setError('');
    setAllPages(null);
    try {
      const { data } = await api.post('/api/seo/seo-analyze-all', {
        url: propUrl,
        page,
        pageSize: PAGE_SIZE,
      });
      setAllPages(data.pages);
      setCurrentPage(data.currentPage || 1);
      setTotalPages(data.totalPages || 1);
      setTotalUrls(data.totalUrls || 0);
    } catch (err) {
      const message = err.response?.data?.error || err.response?.data?.message;
      setError(message || 'Failed to analyze all pages.');
    }
    setLoadingAll(false);
  };

  useEffect(() => {
    if (propUrl) {
      setUrl(propUrl);
      fetchAllPages(1);
    }
    // eslint-disable-next-line
  }, [propUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    analyze(url);
  };

  const handleRescan = () => {
    if (propUrl) {
      fetchAllPages(currentPage);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchAllPages(newPage);
    }
  };

  const toggleExpand = (pageIdx) => {
    setExpanded((prev) => ({ ...prev, [pageIdx]: !prev[pageIdx] }));
  };

  // Helper to check if an issue is the missing alt issue
  const isMissingAltIssue = (issue) => /image\(s\) missing alt attributes/.test(issue);

  return (
    <div className="seo-tab">
      {!propUrl && (
        <form onSubmit={handleSubmit} className="seo-form">
          <input
            type="url"
            placeholder="Enter website URL (e.g., https://example.com)"
            value={url}
            onChange={e => setUrl(e.target.value)}
            required
            className="seo-input"
          />
          <button
            type="submit"
            disabled={loading}
            className="seo-analyze-btn"
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin" aria-hidden />
                Analyzing...
              </>
            ) : (
              <>
                <i className="fas fa-search" aria-hidden />
                Analyze
              </>
            )}
          </button>
        </form>
      )}

      {propUrl && allPages && (
        <div className="seo-issues-card">
          <div className="seo-issues-header">
            <h2 className="seo-issues-title">
              <i className="fas fa-list-alt seo-issues-title-icon" aria-hidden />
              SEO Issues by Page
            </h2>
            <button
              type="button"
              onClick={handleRescan}
              disabled={loadingAll}
              className="seo-rescan-btn"
            >
              {loadingAll ? (
                <>
                  <i className="fas fa-spinner fa-spin" aria-hidden />
                  Scanning...
                </>
              ) : (
                <>
                  <i className="fas fa-sync-alt" aria-hidden />
                  Rescan All Pages
                </>
              )}
            </button>
          </div>
          <div className="seo-table-wrapper">
            <table className="seo-table">
              <thead>
                <tr>
                  <th style={{ width: '35%' }}>
                    <i className="fas fa-link" style={{ marginRight: '8px' }} aria-hidden />
                    Page URL
                  </th>
                  <th style={{ width: '65%' }}>
                    <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px', color: '#a00' }} aria-hidden />
                    SEO Issues
                  </th>
                </tr>
              </thead>
              <tbody>
                {allPages.map((page, idx) => (
                  <React.Fragment key={idx}>
                    <tr>
                      <td className="seo-url-cell">
                        <a
                          href={page.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="seo-url-link"
                        >
                          <i className="fas fa-external-link-alt" style={{ fontSize: '12px' }} aria-hidden />
                          {page.url}
                        </a>
                      </td>
                      <td>
                        {page.issues.length === 0 ? (
                          <span className="seo-no-issues">
                            <i className="fas fa-check-circle" aria-hidden />
                            No major issues
                          </span>
                        ) : (
                          <ul className="seo-issues-list">
                            {page.issues.map((issue, i) => {
                              if (isMissingAltIssue(issue) && page.missingAltImages && page.missingAltImages.length > 0) {
                                return (
                                  <li key={i} className="seo-expandable-issue">
                                    <div
                                      className="seo-issue-item"
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => toggleExpand(idx)}
                                      onKeyDown={(e) => e.key === 'Enter' && toggleExpand(idx)}
                                      role="button"
                                      tabIndex={0}
                                    >
                                      <i
                                        className="fas fa-chevron-right"
                                        style={{
                                          fontSize: '10px',
                                          marginTop: '6px',
                                          transform: expanded[idx] ? 'rotate(90deg)' : 'rotate(0deg)',
                                          transition: 'transform 0.3s ease'
                                        }}
                                        aria-hidden
                                      />
                                      <span style={{ fontWeight: 600 }}>{issue}</span>
                                    </div>
                                    {expanded[idx] && (
                                      <div className="seo-expandable-content">
                                        <div className="seo-expandable-content-title">
                                          Images missing alt attributes:
                                        </div>
                                        <ul>
                                          {page.missingAltImages.map((imgUrl, j) => (
                                            <li key={j}>
                                              <a
                                                href={imgUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                              >
                                                {imgUrl}
                                              </a>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </li>
                                );
                              } else {
                                return (
                                  <li key={i} className="seo-issue-item">
                                    <i className="fas fa-circle" style={{ fontSize: '6px', marginTop: '8px', flexShrink: 0 }} aria-hidden />
                                    <span>{issue}</span>
                                  </li>
                                );
                              }
                            })}
                          </ul>
                        )}
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {error && (
        <div className="seo-error" role="alert">
          <i className="fas fa-exclamation-circle" aria-hidden />
          {error}
        </div>
      )}

      {loadingAll && !allPages && (
        <div className="seo-loading">
          <span className="seo-spinner" role="status" aria-hidden />
          <p>Loading all pages and SEO issues...</p>
        </div>
      )}

      {allPages && totalPages > 1 && (
        <div className="seo-pagination">
          <button
            type="button"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1 || loadingAll}
            className="seo-pagination-btn"
          >
            <i className="fas fa-chevron-left" aria-hidden />
            Previous
          </button>
          <span className="seo-pagination-info">
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong> ({totalUrls} total pages)
          </span>
          <button
            type="button"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || loadingAll}
            className="seo-pagination-btn"
          >
            Next
            <i className="fas fa-chevron-right" aria-hidden />
          </button>
        </div>
      )}
    </div>
  );
};

export default SeoTab; 